import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import ProcessingStatus from './ProcessingStatus';
import { Skel } from './Skeleton';

/**
 * IssuesBar — the ONE issues strip, shared by Today's Health and Historical
 * Health so the pattern can never drift: every red fact is a chip
 * (LABEL · reason); clicking a chip jumps to the element with that issue's
 * id (the parent owns scrolling + flash via onJump). Quiet green line when
 * nothing needs attention; muted scanning line while verdicts are still
 * loading — a premature green is a wrong verdict, never show one.
 */
// `reviewed`: warnings this user dismissed — grey, never counted, never lost:
// a Show / Hide toggle brings them back into view (Apal 2026-10-29).
const ReviewedToggle = ({ count, open, onToggle, tone }) => (
  <button onClick={onToggle} data-testid="reviewed-toggle"
    className={`ml-auto text-xs underline decoration-dotted ${tone} hover:text-white`}>
    {open ? 'Hide dismissed' : `Show dismissed (${count})`}
  </button>
);

const ReviewedChips = ({ reviewed, onJump }) => (
  <div className="flex items-center flex-wrap gap-2 mt-2" data-testid="reviewed-chips">
    {reviewed.map((iss, i) => (
      <button key={`${iss.id}-${i}`} onClick={() => onJump(iss.id)}
        data-testid={`reviewed-chip-${iss.label}`} title={iss.title || `Jump to ${iss.label}`}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-dark-700/60 text-dark-300 border border-dark-600 hover:text-white transition-colors">
        {iss.label}
        <span className="font-normal text-dark-400">· {iss.reason} · dismissed</span>
      </button>
    ))}
  </div>
);

// `pending`: the checks still running (Today's Health: ['close prices',
// 'models']). Known issues show at once; the bar says what is still being
// checked and never turns green until every check is in (Apal 2026-11-03).
const pendingNote = (pending) => `${pending.length > 1 ? `${pending.slice(0, -1).join(', ')} and ${pending[pending.length - 1]}` : pending[0]} still being checked`;

const IssuesBar = ({ issues, reviewed = [], onJump, scanning = false, scanningWhat, pending = [], className = '' }) => {
  const [showReviewed, setShowReviewed] = useState(false);
  const toggle = () => setShowReviewed(v => !v);
  const waiting = pending.length > 0;
  return (
    <div className={className} data-testid="issues-bar">
      {scanning ? (
        <ProcessingStatus active title="Checking all markets" what={scanningWhat} className="" testId="issues-scanning" />
      ) : waiting && issues.length === 0 ? (
        <div className="px-4 py-2.5 rounded-lg bg-dark-700/40 border border-dark-600/40 text-sm" data-testid="issues-pending">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-xs font-bold text-dark-400 uppercase tracking-wider mr-1">Issues</span>
            <Skel className="h-[26px] w-40" rounded="rounded-full" />
            <Skel className="h-[26px] w-32" rounded="rounded-full" />
            <span className="text-[11px] text-dark-400 whitespace-nowrap">· {pendingNote(pending)}</span>
          </div>
        </div>
      ) : issues.length === 0 ? (
        <div className="px-4 py-2.5 rounded-lg bg-green-500/10 border border-green-500/20 text-sm text-green-400" data-testid="issues-clear">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={15} />
            Nothing needs attention — all checks green
            {reviewed.length > 0 && <ReviewedToggle count={reviewed.length} open={showReviewed} onToggle={toggle} tone="text-green-400/70" />}
          </div>
          {showReviewed && reviewed.length > 0 && <ReviewedChips reviewed={reviewed} onJump={onJump} />}
        </div>
      ) : (
        <div className="px-4 py-3 rounded-lg bg-red-500/[0.07] border border-red-500/30">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-xs font-bold text-red-400 uppercase tracking-wider mr-1">
              {issues.length} issue{issues.length === 1 ? '' : 's'}
            </span>
            {issues.map((iss, i) => (
              <button key={`${iss.id}-${i}`} onClick={() => onJump(iss.id)}
                data-testid={`issue-chip-${iss.label}`}
                title={iss.title || `Jump to ${iss.label}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/15 text-red-300 border border-red-400/40 hover:bg-red-500/25 hover:text-white transition-colors">
                {iss.label}
                <span className="font-normal text-red-400/80">· {iss.reason}</span>
                <span aria-hidden className="text-red-400/70">↓</span>
              </button>
            ))}
            {waiting && (
              <span className="text-[11px] text-dark-400 whitespace-nowrap" data-testid="issues-pending-note">· {pendingNote(pending)}</span>
            )}
            {reviewed.length > 0 && <ReviewedToggle count={reviewed.length} open={showReviewed} onToggle={toggle} tone="text-dark-400" />}
          </div>
          {showReviewed && reviewed.length > 0 && <ReviewedChips reviewed={reviewed} onJump={onJump} />}
        </div>
      )}
    </div>
  );
};

export default IssuesBar;
